import { useState, useEffect } from "react";
import { useWorkorderContext } from "../context/WorkorderContext";
import { useWorkorderPermissions, WorkorderField } from "./useWorkorderPermissions";
import toast from "react-hot-toast";
import { api } from "../services/api/api";
import { t } from "i18next";
import { compareObject } from "../services/utils/utils";
import { validator } from "../services/form-validator/form-validator";
import { WorkorderFormValidator } from "../components/data/workorder/WorkorderForm";
import { stripUnusableCompInfos } from "../components/data/compinfos/compinfo-usage";
import { compInfoFieldName } from "../components/data/compinfos/compinfo-errors";
import { getWorkorderTimeError } from "../components/data/workorder/workorder-time-rules";

/**
 * Structure du formulaire pour les informations du workorder
 */
export interface WorkorderFormData {
  notes: string;
  schedule: {
    start: Date | null;
    end: Date | null;
    duration: number;
  };
  time: {
    start: Date | null;
    end: Date | null;
    duration: number;
  };
  travelTime: {
    duration: number;
  };
  onCallDuty: boolean;
  address: {
    address: string;
    addressCompl: string;
    zipCode: string;
    city: string;
    country: string;
    latitude: number;
    longitude: number;
    addressPretty: string;
  };
  technician: EasyTech.Technician | null;
  contact: EasyTech.Contact | null;
  onSiteContact: {
    name: string | undefined,
    phone: string | undefined
  };
}

/**
 * Type pour les modifications de champs imbriqués
 */
type NestedKeys = "schedule.start" | "schedule.end" | "time.start" | "time.end" | "travelTime.duration";

/**
 * Helper to compare two schedule/time objects by their timestamps
 */
const isSameSchedule = (a: any, b: any): boolean => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  
  const getTimestamp = (val: any) => {
    if (!val) return 0;
    return new Date(val).getTime();
  };

  const startA = getTimestamp(a.start);
  const startB = getTimestamp(b.start);
  const endA = getTimestamp(a.end);
  const endB = getTimestamp(b.end);
  
  // Compare timestamps and duration
  return startA === startB && endA === endB && Number(a.duration || 0) === Number(b.duration || 0);
};

/**
 * Hook personnalisé pour gérer les informations d'un workorder
 * @param workorderId - Identifiant du workorder à gérer
 * @returns Méthodes et états pour gérer les informations du workorder
 */
export function useWorkorderInformation(workorderId: string) {
  const { workorder, updateWorkorder } = useWorkorderContext();
  
  const { canEditField } = useWorkorderPermissions(undefined, workorder);
  
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Vérification des permissions
  const canEditNotes = canEditField(WorkorderField.COMP_INFO);
  const canEditSchedule = canEditField(WorkorderField.SCHEDULED_START_DATE) && 
                        canEditField(WorkorderField.SCHEDULED_END_DATE);
  const canEditTime = canEditField(WorkorderField.WO_START_DATE) && 
                    canEditField(WorkorderField.WO_END_DATE);
  const canEditOnCall = canEditField(WorkorderField.ON_CALL);
  const canEditAddress = canEditField(WorkorderField.ADDRESS);
  const canEditTechnician = canEditField(WorkorderField.TECHNICIAN);
  const canEditContact = canEditField(WorkorderField.CONTACT);
  const canEditOnSiteContact = canEditField(WorkorderField.ON_SITE_CONTACT);
  
  // Vérifier si l'utilisateur peut modifier au moins un champ
  const canEditAnyField = canEditNotes || canEditSchedule || canEditTime || canEditOnCall || canEditAddress || canEditTechnician || canEditContact;

  const workorderFieldNames = {
    customer: t("common:customer"),
    contacts: t("common:contacts"),
    address: t("common:address"),
    // Nomme les champs obligatoires restés vides, le bloc en porte une quinzaine
    compInfos: compInfoFieldName(t),
    schedule: t("workorder:schedule"),
    // The real dates are refused for a reason worth naming, so the toast carries it in place of the label
    time: (value: EasyTech.Workorder) => getWorkorderTimeError(value?.time, t) || t("workorder:time")
  };
  
  // State du formulaire
  const [formData, setFormData] = useState<WorkorderFormData>({
    notes: workorder?.notes || "",
    schedule: {
      start: workorder?.schedule?.start ? new Date(workorder.schedule.start) : null,
      end: workorder?.schedule?.end ? new Date(workorder.schedule.end) : null,
      duration: workorder?.schedule?.duration 
        ? (workorder.schedule.duration > 10000 ? Math.round(workorder.schedule.duration / (1000 * 60)) : workorder.schedule.duration)
        : 0
    },
    time: {
      start: workorder?.time?.start ? new Date(workorder.time.start) : null,
      end: workorder?.time?.end ? new Date(workorder.time.end) : null,
      duration: workorder?.time?.duration 
        ? (workorder.time.duration > 10000 ? Math.round(workorder.time.duration / (1000 * 60)) : workorder.time.duration)
        : 0
    },
    travelTime: {
      duration: workorder?.travelTime?.duration || 0
    },
    onCallDuty: workorder?.onCallDuty || false,
    address: {
      address: workorder?.address?.address || "",
      addressCompl: workorder?.address?.addressCompl || "",
      zipCode: workorder?.address?.zipCode || "",
      city: workorder?.address?.city || "",
      country: workorder?.address?.country || "",
      latitude: workorder?.address?.latitude || 0,
      longitude: workorder?.address?.longitude || 0,
      addressPretty: workorder?.address?.addressPretty || ""
    },
    technician: workorder?.technician || null,
    contact: workorder?.contacts && workorder?.contacts.length > 0 ? workorder.contacts[0].contact : null,
    onSiteContact: {
      name: workorder?.onSiteContact?.name || undefined,
      phone: workorder?.onSiteContact?.phone || undefined
    }
  });
  
  /**
   * Met à jour le formData lorsque workorder change
   */
  useEffect(() => {
    if (workorder && !isEditing) {
      setFormData({
        notes: workorder.notes || "",
        schedule: {
          start: workorder.schedule?.start ? new Date(workorder.schedule.start) : null,
          end: workorder.schedule?.end ? new Date(workorder.schedule.end) : null,
          duration: workorder?.schedule?.duration 
            ? (workorder.schedule.duration > 10000 ? Math.round(workorder.schedule.duration / (1000 * 60)) : workorder.schedule.duration)
            : 0
        },
        time: {
          start: workorder.time?.start ? new Date(workorder.time.start) : null,
          end: workorder.time?.end ? new Date(workorder.time.end) : null,
          duration: workorder?.time?.duration 
            ? (workorder.time.duration > 10000 ? Math.round(workorder.time.duration / (1000 * 60)) : workorder.time.duration)
            : 0
        },
        travelTime: {
          duration: workorder.travelTime?.duration || 0
        },
        onCallDuty: workorder.onCallDuty || false,
        address: {
          address: workorder.address?.address || "",
          addressCompl: workorder.address?.addressCompl || "",
          zipCode: workorder.address?.zipCode || "",
          city: workorder.address?.city || "",
          country: workorder.address?.country || "",
          latitude: workorder.address?.latitude || 0,
          longitude: workorder.address?.longitude || 0,
          addressPretty: workorder.address?.addressPretty || ""
        },
        technician: workorder.technician || null,
        contact: workorder.contacts && workorder.contacts.length > 0 ? workorder.contacts[0].contact : null,
        onSiteContact: {
          name: workorder?.onSiteContact?.name || undefined,
          phone: workorder?.onSiteContact?.phone || undefined
        }
      });
    }
  }, [workorder, isEditing]);
  
  /**
   * Calcule la durée entre deux dates en minutes
   * @param start - Date de début
   * @param end - Date de fin
   * @returns Durée en minutes
   */
  const calculateDuration = (start: Date, end: Date): number => {
    if (!(start instanceof Date) || !(end instanceof Date) || isNaN(start.getTime()) || isNaN(end.getTime())) {
      return 0;
    }
    
    const diffMs = end.getTime() - start.getTime();
    
    return Math.max(0, Math.round(diffMs / (1000 * 60)));
  };
  
  /**
   * Gère les changements dans les champs de texte
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...(prev as any)[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  /**
   * Gère les changements de durée directement sans recalculer les dates
   */
  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    let duration = 0;
    if (value && value.includes(":")) {
      const [hours, minutes] = value.split(":").map(Number);
      duration = (hours * 60) + minutes;
    } else {
      duration = parseInt(value) || 0;
    }
    
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...(prev as any)[parent],
          [child]: duration
        }
      }));
    }
  };
  
  /**
   * Gère les changements de dates et recalcule la durée si elle est nulle ou zéro
   */
  const handleDateChange = (dateStr: string | null, field: NestedKeys) => {
    const date = dateStr ? new Date(dateStr) : null;
    const [parent, child] = field.split(".");
    
    setFormData(prev => {
      const newFormData = {
        ...prev,
        [parent]: {
          ...(prev as any)[parent],
          [child]: date
        }
      };
      
      const parentObj = (newFormData as any)[parent];
      if (parentObj.start && parentObj.end) {
        const startDate = parentObj.start instanceof Date ? parentObj.start : new Date(parentObj.start);
        const endDate = parentObj.end instanceof Date ? parentObj.end : new Date(parentObj.end);
        
        if (parent === "schedule" && !isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
          parentObj.duration = calculateDuration(startDate, endDate);
        } else if (parent === "time" && (!parentObj.duration || parentObj.duration === 0) && !isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
          parentObj.duration = calculateDuration(startDate, endDate);
        }
      }
      
      return newFormData;
    });
  };
  
  /**
   * Gère les changements dans les cases à cocher
   */
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };
  
  /**
   * Formate une durée en minutes vers un format lisible
   */
  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours > 0 && mins > 0) {
      return `${hours}h ${mins}min`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${mins}min`;
    }
  };
  
  /**
   * Formate une date pour l'API (ISO 8601)
   */
  const formatDateForApi = (date: Date | null): string | null => {
    if (!date) return null;
    return date.toISOString();
  };
  
  /**
   * Soumet le formulaire pour mise à jour du workorder (INTERNE AU HOOK)
   */
  const handleSubmit = async () => {
    if (!canEditAnyField) {
      toast.error(t("workorder:permissions:noEditPermission"));
      return;
    }
    
    try {
      setIsSaving(true);
      
      const updateData: Record<string, any> = {};
      
      if (canEditNotes) {
        updateData.notes = formData.notes;
      }
      
      if (canEditSchedule) {
        updateData.schedule = {
          start: formatDateForApi(formData.schedule.start),
          end: formatDateForApi(formData.schedule.end),
          duration: formData.schedule.duration * 60 * 1000
        };
      }
      
      if (canEditTime) {
        updateData.time = {
          start: formatDateForApi(formData.time.start),
          end: formatDateForApi(formData.time.end),
          duration: formData.time.duration * 60 * 1000
        };
        
        try {
          await api.put(`users/workorders/${workorderId}/report`, {
            travelTime: {
              start: null,
              end: null,
              duration: formData.travelTime.duration
            }
          });
        } catch (error) {
          console.error("Erreur lors de la mise à jour du temps de trajet:", error);
          toast.error(t("workorder:notifications:travelTimeUpdate:error"));
        }
      }
      
      if (canEditOnCall) {
        updateData.onCallDuty = formData.onCallDuty;
      }
      
      if (canEditAddress) {
        updateData.address = formData.address;
      }
      
      if (canEditContact && formData.contact) {
        updateData.contacts = [{ contact: { id: formData.contact.id }, type: "PRINCIPAL" }];
        
        // Ajouter le customer à la racine si présent dans le contact
        if (formData.contact.customer) {
          updateData.customer = { id: formData.contact.customer.id };
        }
      }
      
      if (canEditOnSiteContact && formData.onSiteContact) {
        updateData.onSiteContact = formData.onSiteContact;
      }

      // Appel API pour mettre à jour les données générales du workorder
      const response = await api.put(`admin/workorders/${workorderId}`, updateData);
      let updatedWorkorder = response as EasyTech.Workorder;
      
      if (canEditTechnician) {
        const technicianChanged = workorder?.technician?.id !== formData.technician?.id;
        
        if (technicianChanged) {
          const assignData = formData.technician 
            ? { id: formData.technician.id } 
            : null;
          
          try {
            await api.put(`admin/workorders/${workorderId}/assign`, assignData);
            
            updatedWorkorder = {
              ...updatedWorkorder,
              technician: formData.technician
            } as EasyTech.Workorder;
            
          } catch (techError) {
            console.error("Erreur lors de l'assignation du technicien:", techError);
            toast.error(t("workorder:messages:technicianAssignError"));
          }
        }
      }
      
      if (response) {
        updateWorkorder(updatedWorkorder);
        toast.success(t("workorder:notifications:update:success"));
        setIsEditing(false);
      }
    } catch (error) {
      console.error("Erreur lors de la mise à jour du workorder:", error);
      toast.error(t("workorder:notifications:update:error"));
    } finally {
      setIsSaving(false);
    }
  };
  
  /**
   * Sauvegarder les modifications depuis un formulaire externe (ex: Drawer)
   * Centralise toute la logique de validation, comparaison et mise à jour
   */
  const saveWorkorderChanges = (newValue: EasyTech.Workorder, originalValue: EasyTech.Workorder = workorder!): Promise<void> => {
    return new Promise((resolve, reject) => {
      const updateData: Record<string, any> = {};
      
      const typeChanged = (newValue?.type?.id ?? null) !== (originalValue?.type?.id ?? null);
      const originChanged = (newValue?.origin?.id ?? null) !== (originalValue?.origin?.id ?? null);
      
      const typePayload = newValue.type ? { id: newValue.type.id } : null;
      const originPayload = newValue.origin ? { id: newValue.origin.id } : null;

      // Validation
      if (!validator.isFormValid(newValue, WorkorderFormValidator)){
        const errorMessage = validator.getFormErrorMessage(newValue, WorkorderFormValidator, workorderFieldNames, t);
        toast.error(errorMessage);
        return reject();
      }

      // Détection des changements
      if (newValue.notes !== originalValue.notes)
        updateData.notes = newValue.notes;
      
      // Schedule : on met à jour uniquement si changé. 
      // Le backend gère la synchro avec la date réelle si nécessaire, 
      // ou le front l'a géré via le formulaire avant l'envoi.
      if (!isSameSchedule(newValue.schedule, originalValue.schedule)) {
        // null JSON serait ignoré par le PUT partiel. Un objet aux dates nulles dit explicitement
        // « effacer la planification ».
        updateData.schedule = (!newValue.schedule?.start && !newValue.schedule?.end)
          ? { start: null, end: null, duration: null }
          : newValue.schedule;
      }
      
      // Time (Réel)
      if (!isSameSchedule(newValue.time, originalValue.time))
        updateData.time = newValue.time;
      
      // Travel Time
      if (!isSameSchedule(newValue.travelTime, originalValue.travelTime))
        updateData.travelTime = newValue.travelTime;

      if (newValue.onCallDuty !== originalValue.onCallDuty)
        updateData.onCallDuty = newValue.onCallDuty;
        
      if (!compareObject(newValue.address, originalValue.address))
        updateData.address = newValue.address;
        
      if (!compareObject(newValue.customer, originalValue.customer))
        updateData.customer = newValue.customer ? { id: newValue.customer.id } : null;
        
      if (!compareObject(newValue.contacts, originalValue.contacts)) {
        updateData.contacts = newValue.contacts?.map((c: any) => ({
          contact: { id: c.contact.id },
          type: c.type
        }));

        // Si contacts modifiés, s'assurer que le client est cohérent
        if (!updateData.customer && newValue.contacts && newValue.contacts.length > 0 && newValue.contacts[0].contact?.customer) {
          updateData.customer = { id: newValue.contacts[0].contact.customer.id };
        }
      }
      
      if (!compareObject(newValue.onSiteContact, originalValue.onSiteContact))
        updateData.onSiteContact = newValue.onSiteContact;
        
      if (typeChanged)
        updateData.type = typePayload;
        
      if (originChanged)
        updateData.origin = originPayload;
        
      if (!compareObject(newValue.technician, originalValue.technician))
        updateData.technician = newValue.technician;
        
      if (newValue.purchaseOrderNumber !== originalValue.purchaseOrderNumber)
        updateData.purchaseOrderNumber = newValue.purchaseOrderNumber;

      // Vérifier si des changements ont été détectés dans les compInfos
      let compInfosChanged = false;
      if (!compareObject(newValue.compInfos, originalValue.compInfos)) {
        compInfosChanged = true;
      }

      // Si aucune donnée n'a changé
      if (Object.keys(updateData).length === 0 && !compInfosChanged) {
        toast.success(t("workorder:notifications:update:success"));
        return resolve();
      }

      // Promesse de mise à jour principale (si nécessaire)
      const mainUpdatePromise = Object.keys(updateData).length > 0
        ? api.put(`admin/workorders/${newValue.id}`, updateData)
        : Promise.resolve(originalValue);

      mainUpdatePromise
        .then((ret) => {
          let updatedWorkorder = ret as EasyTech.Workorder;

          // Si on n'a pas mis à jour via l'API (pas de changement admin), on garde les nouvelles valeurs locales
          // pour les types/origins qui ne sont pas retournés par l'API report
          if (typeChanged || originChanged) {
             updatedWorkorder = {
               ...updatedWorkorder,
               ...(typeChanged ? { type: newValue.type } : {}),
               ...(originChanged ? { origin: newValue.origin } : {})
             } as EasyTech.Workorder;
          }

          if (compInfosChanged) {
            const comp = structuredClone(newValue.compInfos);
            
            // Dédupliquer les selects et inputs par compInfo.id
            if (comp.selects) {
              comp.selects = comp.selects.filter((item, index, self) =>
                index === self.findIndex(x => x.compInfo.id === item.compInfo.id)
              );
            }
            if (comp.inputs) {
              comp.inputs = comp.inputs.filter((item, index, self) =>
                index === self.findIndex(x => x.compInfo.id === item.compInfo.id)
              );
            }

            // Voir stripUnusableCompInfos : le back nous renvoie aussi les compInfos vides
            const payload = stripUnusableCompInfos(comp, originalValue?.status);

            // Construction du payload pour le rapport
            // Note: On inclut time et travelTime s'ils sont présents pour assurer la cohérence
            const reportPayload: any = {
              compInfos: payload
            };
            
            // Si time ou travelTime ont changé, on s'assure qu'ils sont synchronisés dans le rapport
            if (originalValue.time) reportPayload.time = originalValue.time;
            if (originalValue.travelTime) reportPayload.travelTime = originalValue.travelTime;

            // Ajout des autres champs du report existant
            if (originalValue.report) {
              Object.keys(originalValue.report).forEach(key => {
                if (key !== "compInfos") {
                  reportPayload[key] = originalValue.report[key];
                }
              });
            }

            api.put(`users/workorders/${newValue.id}/report`, reportPayload)
              .then(() => {
                const finalWorkorder = {
                  ...updatedWorkorder,
                  compInfos: {
                    inputs: payload.inputs,
                    selects: payload.selects
                  }
                };
                updateWorkorder(finalWorkorder);
                toast.success(t("workorder:notifications:update:success"));
                return resolve();
              }).catch((error) => {
                console.error("Erreur lors de la mise à jour des compInfos:", error);
                toast.error(t("workorder:notifications:update:error"));
                return reject();
              });
          } else {
            // Mise à jour standard sans compInfos
            if (Object.keys(updateData).length > 0) {
              updateWorkorder(updatedWorkorder);
              toast.success(t("workorder:notifications:update:success"));
            }
            return resolve();
          }
        }).catch((error) => {
          console.error("Erreur lors de la mise à jour du workorder:", error);
          toast.error(t("workorder:messages:updateError"));
          return reject();
        });
    });
  };

  /**
   * Annule les modifications et réinitialise le formulaire
   */
  const handleCancel = () => {
    if (workorder) {
      setFormData({
        notes: workorder.notes || "",
        schedule: {
          start: workorder.schedule?.start ? new Date(workorder.schedule.start) : null,
          end: workorder.schedule?.end ? new Date(workorder.schedule.end) : null,
          duration: workorder?.schedule?.duration 
            ? (workorder.schedule.duration > 10000 ? Math.round(workorder.schedule.duration / (1000 * 60)) : workorder.schedule.duration)
            : 0
        },
        time: {
          start: workorder.time?.start ? new Date(workorder.time.start) : null,
          end: workorder.time?.end ? new Date(workorder.time.end) : null,
          duration: workorder?.time?.duration 
            ? (workorder.time.duration > 10000 ? Math.round(workorder.time.duration / (1000 * 60)) : workorder.time.duration)
            : 0
        },
        travelTime: {
          duration: workorder.travelTime?.duration || 0
        },
        onCallDuty: workorder.onCallDuty || false,
        address: {
          address: workorder.address?.address || "",
          addressCompl: workorder.address?.addressCompl || "",
          zipCode: workorder.address?.zipCode || "",
          city: workorder.address?.city || "",
          country: workorder.address?.country || "",
          latitude: workorder.address?.latitude || 0,
          longitude: workorder.address?.longitude || 0,
          addressPretty: workorder.address?.addressPretty || ""
        },
        technician: workorder.technician || null,
        contact: workorder.contacts && workorder.contacts.length > 0 ? workorder.contacts[0].contact : null,
        onSiteContact: {
          name: workorder?.onSiteContact?.name || undefined,
          phone: workorder?.onSiteContact?.phone || undefined
        }
      });
    }
    setIsEditing(false);
  };
  
  /**
   * Démarre le mode édition
   */
  const startEditing = () => {
    setIsEditing(true);
  };
  
  // Retourne toutes les fonctions et états nécessaires
  return {
    formData,
    setFormData,
    isEditing,
    isSaving,
    canEditNotes,
    canEditSchedule,
    canEditTime,
    canEditOnCall,
    canEditAddress,
    canEditTechnician,
    canEditContact,
    canEditAnyField,
    handleChange,
    handleDateChange,
    handleCheckboxChange,
    handleSubmit,
    handleCancel,
    startEditing,
    formatDuration,
    calculateDuration,
    handleDurationChange,
    updateWorkorder,
    saveWorkorderChanges
  };
} 